import { fetchGetTaskStatus } from '~/backend/connector-api/connector-components';
import { TaskPresentation } from '~/backend/connector-api/connector-schemas';
import { downloadErrors, downloadResults } from '~/backend/connector-api/utils';
import { FINISHED_JOB_STATUSES, Job } from '~/state/atoms/jobAtoms';

const DEFAULT_POLLING_INTERVAL = 2000;

/**
 * Periodically polls the connector API for a task's status until it reaches a finished state.
 * It reports status changes via a callback as it polls.
 * @param job - The job being processed, which contains the task ID.
 * @param onStatusChange - A callback function invoked with the job ID and new status when a change is detected.
 * @param intervalMs - The time in milliseconds between polling attempts.
 * @returns A promise that resolves with the final `TaskOutput` when the job is complete.
 * @throws Rejects the promise if polling fails.
 */
export async function pollTaskStatus(
    job: Job,
    onStatusChange: (
        jobId: string,
        status: Job['status'],
        errorMessage?: string
    ) => void,
    intervalMs = DEFAULT_POLLING_INTERVAL
): Promise<TaskPresentation> {
    const jobId = job.id;
    return new Promise((resolve, reject) => {
        const poll = async () => {
            try {
                const statusResponse = await fetchGetTaskStatus({
                    pathParams: {
                        taskID: jobId,
                    },
                });

                if (FINISHED_JOB_STATUSES.includes(statusResponse.status!)) {
                    resolve(statusResponse);
                } else {
                    if (job.status !== statusResponse.status) {
                        onStatusChange(jobId, statusResponse.status!);
                    }
                    setTimeout(poll, intervalMs);
                }
            } catch (err) {
                reject(new Error(`Job ${jobId}: Polling failed.`));
            }
        };
        void poll();
    });
}

export type ConnectorDownloadedResults = {
    files: File[];
    errors: File[];
    downloadErrors: string[];
};

/**
 * Downloads all result files and error files for a completed connector job.
 * It handles both successful results and errors concurrently.
 * @param pollData - The final `TaskOutput` from the polling process, containing result and error IDs.
 * @returns A promise that resolves to a `ConnectorDownloadedResults` object, which aggregates all downloaded files, error files, and any errors that occurred during the download process.
 */
export async function downloadConnectorResults(
    pollData: TaskPresentation
): Promise<ConnectorDownloadedResults> {
    const allDownloadedFiles: File[] = [];
    const allBackendErrors: File[] = [];
    const allDownloadAndProcessingErrors: string[] = [];

    const errorIds = pollData.errors
        ? pollData.errors
              .map((er) => er.filename)
              .filter((id): id is string => id != null && id !== '')
        : [];
    if (errorIds.length > 0) {
        const errorOutcome = await downloadErrors(pollData.id!, errorIds);
        allBackendErrors.push(...errorOutcome.files);
        allDownloadAndProcessingErrors.push(...errorOutcome.errors);
    }

    const resultIds = pollData.results
        ? pollData.results
              .map((res) => res.filename)
              .filter((id): id is string => id != null)
        : [];
    if (resultIds.length > 0) {
        const resultOutcome = await downloadResults(pollData.id!, resultIds);
        allDownloadedFiles.push(...resultOutcome.files);
        allDownloadAndProcessingErrors.push(...resultOutcome.errors);
    }

    return {
        files: allDownloadedFiles,
        errors: allBackendErrors,
        downloadErrors: allDownloadAndProcessingErrors,
    };
}
